import React, { useEffect, useMemo, useState } from "react";
import CustomBtn from "../../components/btn/CustomBtn";
import { Button, Dropdown, Modal, Table, Tag } from "antd";
import { IconArrowRight, IconPlus } from "@tabler/icons-react";
import CustomInput from "../../components/input/CustomInput";
import PageHeader from "../../components/header/PageHeader";
import api from "../../api";
import { formatDateTime, validateEmail } from "../../utils/helpers";
import { useToast } from "../../components/toast";
import ConfirmModal from "../../components/modal/ConfirmModal";
import CustomSelect from "../../components/select/CustomSelect";

export default function Staff() {
  const [showModal, setShowModal] = useState(false);
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    limit: 10,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState(null);
  const [originalRoleId, setOriginalRoleId] = useState(null);

  const toast = useToast();

  const fetchRoles = async () => {
    const res = await api.get_roles();
    if (res?.status === 200) setRoles(res.data.data);
  };

  const fetchData = async ({
    page = pagination.current,
    limit = pagination.limit,
  }) => {
    setLoading(true);
    const res = await api.get_users({
      page,
      limit,
      role: "admin",
    });
    if (res?.status === 200) {
      setData(res.data.data);
      setPagination({
        current: page,
        limit,
        total: res.data.pagination?.total || 0,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData({ page: 1 });
    fetchRoles();
  }, []);

  const columns = useMemo(
    () => [
      {
        title: "И-мэйл",
        dataIndex: "email",
        key: "email",
      },
      {
        title: "Эрх",
        key: "role",
        render: (_, r) =>
          r.Role ? (
            <Tag color={r.Role.key === "super_admin" ? "gold" : "blue"}>
              {r.Role.name}
            </Tag>
          ) : (
            <Tag>Эрхгүй</Tag>
          ),
      },

      {
        title: "Төлөв",
        dataIndex: "active",
        key: "active",
        render: (active) => (
          <Tag color={active ? "green" : "red"}>
            {active ? "Идэвхтэй" : "Идэвхгүй"}
          </Tag>
        ),
      },
      {
        title: "Огноо",
        dataIndex: "created_at",
        key: "created_at",
        render: (e) => formatDateTime(e) || "-",
      },
      {
        title: "Үйлдэл",
        key: "actions",
        width: 150,
        render: (_, record) => {
          const items = (record) => [
            {
              key: "edit",
              label: (
                <span style={{ color: "black", fontSize: "13px" }}>Засах</span>
              ),
            },
            {
              key: "delete",
              label: (
                <span style={{ color: "red", fontSize: "13px" }}>
                  Идэвхгүй болгох
                </span>
              ),
            },
          ];

          return (
            <Dropdown
              menu={{
                items: items(record),
                onClick: ({ key }) => {
                  if (key === "delete") {
                    // console.log("Устгах:", record);
                    setSelectedId(record?.id);
                    setVisible(true);
                  } else if (key === "edit") {
                    setSelectedId(record?.id);
                    setEmail(record?.email || "");
                    setRoleId(record?.Role?.id || null);
                    setOriginalRoleId(record?.Role?.id || null);
                    setShowModal(true);
                  }
                },
              }}
              trigger={["click"]}
            >
              <Button variant="filled" color="default" style={{ fontSize: 12 }}>
                Үйлдэл
                <IconArrowRight size={12} />
              </Button>
            </Dropdown>
          );
        },
      },
    ],
    [pagination.current, pagination.pageSize],
  );

  const onTableChange = (pag) => {
    fetchData({ page: pag.current, limit: pag.limit });
  };

  const clearHandler = () => {
    setEmail("");
    setPassword("");
    setRoleId(null);
    setOriginalRoleId(null);
    setSelectedId(null);
    setShowModal(false);
  };

  const submitHandler = async () => {
    let userId = selectedId;

    // 1) Ажилтан үүсгэх / засах
    if (selectedId) {
      const res = await api.update_staff(selectedId, {
        email,
        ...(password ? { password } : {}),
      });
      if (res?.status !== 200)
        return toast(res?.data?.message || "Алдаа гарлаа", "error");
    } else {
      const res = await api.create_staff({ email, password });
      if (res?.status !== 201)
        return toast(res?.data?.message || "Алдаа гарлаа", "error");
      userId = res.data.data?.id;
    }

    // 2) Эрх өөрчлөгдсөн бол оноох
    if (userId && (roleId || null) !== (originalRoleId || null)) {
      const r = await api.assign_role(userId, roleId || null);
      if (r?.status !== 200) {
        toast(r?.data?.message || "Эрх оноход алдаа гарлаа", "error");
        clearHandler();
        return fetchData({ page: 1 });
      }
    }

    clearHandler();
    fetchData({ page: 1 });
    toast("Амжилттай", "success");
  };

  const deactivateHandler = async () => {
    if (selectedId) {
      const res = await api.deactivate(selectedId);
      //   console.log("staff res --->", res);
      if (res.status === 200) {
        setSelectedId(null);
        fetchData({ page: 1 });
        setVisible(false);
        toast("Амжилттай", "success");
      } else {
        toast(res.data.message || "Алдаа гарлаа", "error");
      }
    }
  };

  return (
    <>
      <PageHeader
        title={"Ажилчид"}
        right={
          <CustomBtn
            title={"Ажилтан нэмэх"}
            icon={<IconPlus size={18} />}
            onClick={() => setShowModal(true)}
          />
        }
      />
      <div>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          rowHoverable={false}
          pagination={{
            current: pagination.current,
            limit: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (t) => `Нийт: ${t}`,
          }}
          onChange={onTableChange}
        />
      </div>

      <Modal
        open={showModal}
        width={400}
        footer={
          <div className="d-flex gap-2 justify-content-end">
            <CustomBtn
              type="secondary"
              title={"Болих"}
              onClick={() => clearHandler()}
            />
            <CustomBtn
              title={"Хадгалах"}
              disabled={
                selectedId
                  ? !validateEmail(email)
                  : !(validateEmail(email) && password)
              }
              onClick={submitHandler}
            />
          </div>
        }
        closable={false}
        // onCancel={handleClose}
        title={selectedId ? "Ажилтан засах" : "Ажилтан нэмэх"}
      >
        <div className="d-flex flex-column gap-2">
          <CustomInput
            required
            title={"И-мэйл"}
            placeholder="-"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <CustomInput
            isPassword
            required={!selectedId}
            title={selectedId ? "Шинэ нууц үг (солих бол)" : "Нууц үг"}
            placeholder="-"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <CustomSelect
            title="Эрх"
            placeholder="Эрх сонгох"
            value={roleId}
            onChange={(v) => setRoleId(v ?? null)}
            options={roles.map((r) => ({ value: r.id, label: r.name }))}
          />
        </div>
      </Modal>

      <ConfirmModal
        variant="warning"
        title="Итгэлтэй байна уу?"
        description={
          <>
            '
            <span style={{ fontWeight: 600, color: "#000" }}>
              Идэвхгүй болгох
            </span>
            ' үйлдэл нь буцаах боломжгүйг анхаарна уу!
          </>
        }
        showModal={visible}
        setShowModal={setVisible}
        submitHandler={deactivateHandler}
      />
    </>
  );
}
